import React, { useContext, useEffect, useRef, useState } from "react";
import tw from "twin.macro";
import AppContext from "@components/AppContext";
import Container from "@components/Container";
import Isi from "@klickinc/kfs-components-isi-dual";
import ExitModal from "@components/ExitModal";
import LinkToPDF from "@components/LinkToPDF";

import expandIcon from "@images/top-arrow.svg";

const IsiSection = ({ headerRef }) => {
  const { stickyIsiRef, isHeaderVisible, showBanner } = useContext(AppContext);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isiContent = {
    content1: {
      header: "Indications & Usage",
      content: (
        <>
          <h3 tw="text-magenta font-bold text-[1.1875rem] leading-[1.3] tracking-[0.01188rem] mt-1">
            Indications
          </h3>
          <p tw="mt-1 tracking-normal">
            XERAVA&reg; (eravacycline) for injection is indicated for the
            treatment of complicated intra-abdominal infections (cIAI) caused by
            susceptible microorganisms:{" "}
            <span tw="italic">
              Escherichia coli, Klebsiella pneumoniae, Citrobacter freundii,
              Enterobacter cloacae, Klebsiella oxytoca, Enterococcus faecalis,
              Enterococcus faecium, Staphylococcus aureus, Streptococcus
              anginosus
            </span>{" "}
            group, <span tw="italic">Clostridium perfringens, Bacteroides</span>{" "}
            species, and <span tw="italic">Parabacteroides distasonis</span> in
            patients 18&nbsp;years or older.
          </p>
          <p tw="tracking-normal">
            <span tw="block underline mt-4">Limitations of Use</span> XERAVA is
            not indicated for the treatment of complicated urinary tract
            infections (cUTI).
          </p>
          <h3 tw="text-magenta font-bold text-[1.1875rem] leading-[1.3] tracking-[0.01188rem] mt-4">
            Usage
          </h3>
          <p tw="tracking-normal">
            To reduce the development of drug-resistant bacteria and maintain
            the effectiveness of XERAVA and other antibacterial drugs, XERAVA
            should be used only to treat or prevent infections that are proven
            or strongly suspected to be caused by susceptible bacteria. When
            culture and susceptibility information are available, they should be
            considered in selecting or modifying antibacterial therapy. In the
            absence of such data, local epidemiology and susceptibility patterns
            may contribute to the empiric selection of therapy.
          </p>
        </>
      ),
    },
    content2: {
      header: "Important Safety Information",
      content: (
        <>
          <p tw="mt-2.5 tracking-normal">
            XERAVA is contraindicated for use in patients with known
            hypersensitivity to eravacycline, tetracycline-class antibacterial
            drugs, or to any of the excipients. Life-threatening
            hypersensitivity (anaphylactic) reactions have been reported with
            XERAVA.
          </p>
          <p tw="mt-4 tracking-normal">
            The use of XERAVA during tooth development (last half of pregnancy,
            infancy and childhood to the age of 8 years) may cause permanent
            discoloration of the teeth (yellow-gray-brown) and enamel
            hypoplasia.
          </p>
          <p tw="mt-4 tracking-normal">
            The use of XERAVA during the second and third trimester of
            pregnancy, infancy and childhood up to the age of 8 years may cause
            reversible inhibition of bone growth.
          </p>
          <p tw="mt-4 tracking-normal">
            <span tw="italic">Clostridioides difficile</span> associated
            diarrhea (CDAD) has been reported with use of nearly all
            antibacterial agents, and may range in severity from mild diarrhea
            to fatal colitis.
          </p>
          <p tw="mt-4 tracking-normal">
            The most common adverse reactions observed in clinical trials
            (incidence ≥3%) were infusion site reactions (7.7%), nausea (6.5%),
            and vomiting (3.7%).
          </p>
          <p tw="mt-4 tracking-normal">
            XERAVA is structurally similar to tetracycline-class antibacterial
            drugs and may have similar adverse reactions. Adverse reactions
            including photosensitivity, pseudotumor cerebri, and anti-anabolic
            action which has led to increased BUN, azotemia, acidosis,
            hyperphosphatemia, pancreatitis, and abnormal liver function tests,
            have been reported for other tetracycline-class antibacterial drugs,
            and may occur with XERAVA. Discontinue XERAVA if any of these
            adverse reactions are suspected.
          </p>

          <p tw="font-bold mt-4 tracking-normal">
            You are encouraged to report negative side eﬀects of prescription
            drugs to the FDA. To report SUSPECTED ADVERSE REACTIONS,
            please&nbsp;contact:
          </p>
          <div tw="lg:(flex justify-between gap-4)">
            <div>
              <p tw="mt-4">
                Innoviva Specialty Therapeutics,&nbsp;Inc.<sup>&trade;</sup>{" "}
                <br />
                <a href="tel:+1-800-651-3861">
                  1&#8209;800&#8209;651&#8209;3861
                </a>
              </p>
              <a
                href="mailto:medinfo@istx.com"
                tw="font-bold text-magenta underline"
              >
                medinfo@istx.com
              </a>
            </div>
            <div>
              <p tw="mt-4">
                U.S. Food and Drug Administration <br />
                <a href="tel:+1-800-FDA-1088">
                  1&#8209;800&#8209;FDA&#8209;1088
                </a>
              </p>
              <ExitModal
                url="https://www.fda.gov/safety/medwatch-fda-safety-information-and-adverse-event-reporting-program"
                tw="font-bold text-magenta underline"
              >
                www.fda.gov/medwatch
              </ExitModal>
            </div>
          </div>
          <p tw="mt-4 tracking-normal">
            Before administering, please see the{" "}
            <LinkToPDF
              url="/pi"
              fileLocation="isi"
              tw="font-bold text-magenta underline"
            >
              Full&nbsp;Prescribing Information
            </LinkToPDF>{" "}
            for XERAVA.
          </p>
        </>
      ),
    },
  };

  const h2StickyStyle = tw`flex justify-between items-start text-magenta font-black text-[1.375rem] leading-[1.1] tracking-[0.01375rem] pt-[0.81rem] lg:(pb-1.5 pt-0)`;
  const drawerStyle = tw`bg-periwinkle50 transition-[max-height] duration-300 ease-in-out lg:(pt-[5.2rem] pb-12)`;

  const drawerOneHeaderRef = useRef(null);
  const drawerTwoHeaderRef = useRef(null);

  useEffect(() => {
    setHeaderHeight(headerRef?.current?.getBoundingClientRect().bottom || 0);
  }, [isHeaderVisible, headerRef, showBanner]);

  return (
    <Isi>
      {({ isBodyISIVisible, isDrawer1Expanded, isDrawer2Expanded }) => (
        <>
          <Isi.StickyWrapper
            options={{ removeDefaultStyles: true }}
            css={[
              tw`fixed bottom-0 w-full bg-periwinkle50 overflow-hidden z-40 transition-all duration-300 ease-in-out lg:(max-h-[11.4375rem] h-full)`,
              isBodyISIVisible && tw`hidden`,
              (isDrawer1Expanded || isDrawer2Expanded) &&
              `
              max-height: calc(
                100dvh - ${headerHeight}px
              ) !important;
              @media (min-width: 1024px) {
                overflow-y: scroll;
              }
            `,
            ]}
          >
            <Container
              tw="max-[1023px]:(mx-0 max-w-none) lg:(flex justify-between gap-[2.375rem] max-h-full)"
              ref={stickyIsiRef}
            >
              {/* desktop headers */}
              <div tw="hidden lg:(bg-periwinkle50 flex justify-between gap-[2.375rem] fixed w-full max-w-[57.75rem] pt-[3.19rem] z-20)">
                <h2 css={[h2StickyStyle, tw`w-[34%]`]}>
                  <span tw="uppercase">{isiContent.content1.header}</span>
                </h2>
                <h2 css={[h2StickyStyle, tw`grow`]}>
                  <span tw="uppercase">{isiContent.content2.header}</span>
                  <Isi.Toggle2
                    className="group"
                    tw="absolute flex justify-center w-11 h-11 -right-2 top-[1.875rem] [>*]:(pointer-events-none)"
                  >
                    <span tw="transition-all bg-magenta w-[1.875rem] h-[1.875rem] flex items-center justify-center rounded-[50%] hover:(bg-[#990659])">
                      <img
                        src={expandIcon}
                        alt=""
                        css={[isDrawer2Expanded && tw`rotate-180`]}
                      />
                    </span>
                  </Isi.Toggle2>
                </h2>
              </div>
              {/* end desktop headers */}

              <Isi.Drawer1
                options={{ removeDefaultStyles: true }}
                css={[
                  !isDrawer1Expanded &&
                  tw`max-h-36 overflow-hidden lg:( max-h-full)`,
                  isDrawer1Expanded &&
                  `
                  max-height: calc(
                    100dvh - ${headerHeight}px - 9.5rem
                  );
                `,
                  drawerStyle,
                  tw`lg:(w-[34%] min-w-[34%] overflow-visible)`,
                ]}
              >
                <Container tw="pr-2.5 lg:(pr-0)">
                  <h2
                    css={[
                      h2StickyStyle,
                      tw`relative pb-1 bg-periwinkle50 lg:hidden`,
                    ]}
                    ref={drawerOneHeaderRef}
                  >
                    <span tw="uppercase">{isiContent.content1.header}</span>
                    <Isi.Toggle1
                      className="group"
                      tw="absolute flex justify-center w-11 h-11 -right-6 top-2.5"
                    >
                      <span tw="transition-all bg-magenta w-[1.875rem] h-[1.875rem] flex items-center justify-center rounded-[50%] hover:(bg-[#990659])">
                        <img
                          src={expandIcon}
                          alt=""
                          css={[isDrawer1Expanded && tw`rotate-180`]}
                        />
                      </span>
                    </Isi.Toggle1>
                  </h2>
                  <Isi.Content
                    options={{ removeDefaultStyles: true }}
                    css={[
                      tw`max-h-full overflow-hidden pr-4 -mr-4 pb-4 leading-[1.25] lg:(pr-0 mr-0 pb-0 transition-all)`,
                      isDrawer1Expanded &&
                      `
                        overflow-y: scroll;
                        max-height: calc(
                        100dvh -
                          (
                            ${headerHeight}px + ${drawerOneHeaderRef?.current?.clientHeight}px + 9.5rem
                          )
                        );
                        @media (min-width: 1024px) {
                          max-height: 100%;
                          overflow-y: hidden;
                        }
                      `,
                    ]}
                  >
                    {isiContent.content1.content}
                  </Isi.Content>
                </Container>
              </Isi.Drawer1>
              <Isi.Drawer2
                options={{ removeDefaultStyles: true }}
                css={[
                  !isDrawer2Expanded &&
                  tw`max-h-[9.5rem] overflow-hidden lg:(max-h-[11.4375rem])`,
                  isDrawer2Expanded &&
                  `
                    max-height: calc(
                      100dvh - ${headerHeight}px - 9rem
                    );
                    @media (min-width: 1024px) {
                      max-height: 100%;
                    }
                  `,
                  tw`border-t-[0.1875rem] border-periwinkle01 relative z-10 lg:(border-0 )`,
                  drawerStyle,
                ]}
              >
                <Container tw="pr-2.5 lg:(pr-0)">
                  <h2
                    css={[h2StickyStyle, tw`pb-2 relative lg:hidden`]}
                    ref={drawerTwoHeaderRef}
                  >
                    <span tw="uppercase">{isiContent.content2.header}</span>
                    <Isi.Toggle2
                      className="group"
                      tw="absolute flex justify-center w-11 h-11 -right-6 top-2.5"
                    >
                      <span tw="transition-all bg-magenta w-[1.875rem] h-[1.875rem] flex items-center justify-center rounded-[50%] hover:(bg-[#990659])">
                        <img
                          src={expandIcon}
                          alt=""
                          css={[isDrawer2Expanded && tw`rotate-180`]}
                        />
                      </span>
                    </Isi.Toggle2>
                  </h2>
                  <Isi.Content
                    options={{ removeDefaultStyles: true }}
                    css={[
                      tw`max-h-full overflow-hidden pr-4 -mr-4 pb-4 leading-[1.25] lg:(pr-0 mr-0 pb-0)`,
                      isDrawer2Expanded &&
                      `
                        overflow-y: scroll;
                        max-height: calc(
                        100dvh -
                          (
                            ${headerHeight}px + ${drawerTwoHeaderRef?.current?.clientHeight}px + 9rem
                          )
                        );
                        @media (min-width: 1024px) {
                          max-height: 100%;
                          overflow-y: hidden;
                        }
                      `,
                    ]}
                  >
                    {isiContent.content2.content}
                  </Isi.Content>
                </Container>
              </Isi.Drawer2>
            </Container>
          </Isi.StickyWrapper>

          <div
            id="isi"
            tw="bg-periwinkle50 scroll-mt-36 pt-[2.0625rem] pb-[2.875rem] lg:(pt-[3.1875rem] pb-[3.375rem] scroll-mt-44)"
          >
            <Container tw="pr-2.5 lg:(pr-0)">
              <Isi.Body tw="leading-[1.25] lg:(flex justify-between gap-[2.375rem])">
                <div tw="lg:(w-[34%] min-w-[34%])">
                  <h2 tw="uppercase font-black text-[1.375rem] leading-[1.1] tracking-[0.01375rem] text-magenta">
                    {isiContent.content1.header}
                  </h2>
                  {isiContent.content1.content}
                </div>
                <div tw="lg:(grow)">
                  <h2 tw="uppercase font-black text-[1.375rem] leading-[1.1] tracking-[0.01375rem] text-magenta mt-7 lg:(mt-0)">
                    {isiContent.content2.header}
                  </h2>
                  {isiContent.content2.content}
                </div>
              </Isi.Body>
            </Container>
          </div>
        </>
      )}
    </Isi>
  );
};

export default IsiSection;
